<template>
  <svg
    class="qr"
    :width="size"
    :height="size"
    :viewBox="`0 0 ${modules} ${modules}`"
    shape-rendering="crispEdges"
    role="img"
    :aria-label="alt"
  >
    <rect :width="modules" :height="modules" fill="#ffffff" />
    <path :d="path" fill="#000000" />
  </svg>
</template>

<script setup>
import { computed } from 'vue'
import qrcode from 'qrcode-generator'

const props = defineProps({
  value: { type: String, required: true },
  size: { type: Number, default: 180 },
  alt: { type: String, default: 'QR code' },
  // Error correction. 'M' is right for a screen photographed at an angle; a
  // label that will live on a warehouse shelf and collect dust and forklift
  // scuffs wants 'Q' or 'H', at the cost of a denser code.
  level: { type: String, default: 'M' },
  // Quiet zone, in modules. The spec asks for 4 and scanners need it: a code
  // printed flush against the edge of a label reads as no code at all.
  quietZone: { type: Number, default: 0 },
})

// Type 0 = pick the smallest version that fits. Level 'M' survives the partial
// occlusion you get photographing a screen at an angle.
const qr = computed(() => {
  const instance = qrcode(0, props.level)
  instance.addData(props.value)
  instance.make()
  return instance
})

const count = computed(() => qr.value.getModuleCount())
const modules = computed(() => count.value + props.quietZone * 2)

// One <path> for the whole code rather than a rect per module: a version-7 QR
// is ~2000 modules, and 2000 DOM nodes is a visible hitch on a slow laptop.
// It is also what makes a page of 40 labels print in one go instead of hanging.
const path = computed(() => {
  const size = count.value
  const offset = props.quietZone
  const parts = []
  for (let row = 0; row < size; row++) {
    for (let column = 0; column < size; column++) {
      if (qr.value.isDark(row, column)) parts.push(`M${column + offset},${row + offset}h1v1h-1z`)
    }
  }
  return parts.join('')
})
</script>

<style scoped>
.qr {
  display: block;
  border-radius: var(--radius-sm);
  /* Always on white, whatever the theme: scanners need the contrast. */
  background: #fff;
  padding: 8px;
}
</style>
